/**
 * Story 3.16 — UI category mapping for `system_settings` keys.
 *
 * Categories are NOT persisted in the database (per Story Decision 4 — keeps
 * the migration footprint minimal and lets us re-shuffle the UI without a
 * schema change). The 6 buckets mirror the story Dev Notes table.
 *
 * Adding a new key:
 *   1. Append it to `SYSTEM_SETTING_KEYS` in
 *      `packages/admin-schemas/src/system-settings.ts` (also add a validator
 *      branch in `validateSettingValue`).
 *   2. Add a `{ key, type, ... }` entry below under the right category.
 *   3. (If new category) extend `CATEGORY_ORDER` + `CATEGORY_LABELS`.
 */

import type { SystemSettingKey } from '@sdr-agend/admin-schemas'
import {
  OPENAI_MODEL_OPTIONS,
  CALENDAR_PROVIDER_OPTIONS,
  SELLER_DISTRIBUTION_RULE_OPTIONS,
} from '@sdr-agend/admin-schemas'

export type SettingCategory =
  | 'operacao'
  | 'lgpd'
  | 'openai'
  | 'prompts'
  | 'reagendamento'
  | 'calendar'

export type EditorType = 'number' | 'string' | 'textarea' | 'boolean' | 'object' | 'array' | 'enum'

export interface SettingMeta {
  key: SystemSettingKey
  category: SettingCategory
  editor: EditorType
  label: string
  hint?: string
  // Bounds only used for numeric editors (UI hints — backend validates).
  min?: number
  max?: number
  step?: number
  // Enum options for `editor: 'enum'`.
  options?: ReadonlyArray<string>
}

/**
 * Display order for categories. Operação first (most-touched), Calendar last
 * (recently added).
 */
export const CATEGORY_ORDER: ReadonlyArray<SettingCategory> = [
  'operacao',
  'lgpd',
  'openai',
  'prompts',
  'reagendamento',
  'calendar',
]

export const CATEGORY_LABELS: Record<SettingCategory, string> = {
  operacao: 'Operação',
  lgpd: 'LGPD',
  openai: 'OpenAI',
  prompts: 'Prompts',
  reagendamento: 'Reagendamento',
  calendar: 'Calendar',
}

/**
 * Per-key UI metadata. The shape mirrors the table in Story 3.16 Dev Notes.
 */
export const SETTING_META: ReadonlyArray<SettingMeta> = [
  // ---------- Operação --------------------------------------------------
  {
    key: 'agent_enabled',
    category: 'operacao',
    editor: 'boolean',
    label: 'IA ativa (liga/desliga geral)',
    hint:
      'Interruptor mestre. Desligado = a IA NÃO responde mensagens (inbound) e ' +
      'NÃO dispara nenhum envio proativo: confirmações, reativação, cold e ' +
      'notificações ao vendedor. Respostas manuais do operador não são afetadas. ' +
      'Use para pausar tudo durante testes.',
  },
  {
    key: 'business_hours',
    category: 'operacao',
    editor: 'object',
    label: 'Horário comercial',
    hint: 'JSON: { timezone, start (HH:mm), end (HH:mm), days: ["mon",...] }',
  },
  {
    key: 'opt_in_text',
    category: 'operacao',
    editor: 'textarea',
    label: 'Texto de opt-in',
    hint: 'Texto livre apresentado no cadastro (LGPD).',
  },
  {
    key: 'handoff_sla_minutes',
    category: 'operacao',
    editor: 'number',
    label: 'SLA handoff (min)',
    min: 1,
    max: 240,
    step: 1,
  },
  {
    key: 'handoff_operators',
    category: 'operacao',
    editor: 'array',
    label: 'Operadores autorizados',
    hint: 'Array JSON de user IDs (UUIDs).',
  },
  // Story 5.4 — SellerDistributor settings live under Operação alongside
  // business_hours + handoff_* because they govern the lead-flow runtime.
  {
    key: 'seller_distribution_rule',
    category: 'operacao',
    editor: 'enum',
    label: 'Regra de distribuição',
    options: SELLER_DISTRIBUTION_RULE_OPTIONS,
    hint: 'Estratégia de seleção de vendedor para leads qualificados (MVP: round_robin).',
  },
  {
    key: 'last_assigned_seller_id',
    category: 'operacao',
    editor: 'string',
    label: 'Cursor round-robin',
    hint:
      'UUID do último vendedor escolhido (null = primeiro lead). ' +
      'Resetar para vazio reinicia o ciclo.',
  },
  // Story 5.5.2 — test phone allowlist surfaced under Operação. Empty by
  // default; non-empty restricts the GHL cron worker to those phones only.
  // Webhook is NOT affected. Operator runbook: leave empty in production.
  {
    key: 'ghl_test_phone_allowlist',
    category: 'operacao',
    editor: 'array',
    label: 'Allowlist GHL cron (telefones de teste)',
    hint:
      'Array JSON de E.164 (ex: ["+5511999999999"]). ' +
      'Vazio = sem restrição. Aplica APENAS ao cron — webhook continua processando qualquer número.',
  },
  // ---------- LGPD ------------------------------------------------------
  {
    key: 'lgpd_retention_days',
    category: 'lgpd',
    editor: 'number',
    label: 'Retenção (dias)',
    min: 1,
    max: 3650,
    step: 1,
  },
  {
    key: 'opt_out_ai_confidence_min',
    category: 'lgpd',
    editor: 'number',
    label: 'Confiança mínima opt-out IA',
    min: 0,
    max: 1,
    step: 0.05,
    hint: 'Score 0..1 — opt-out via IA dispara acima deste threshold.',
  },
  // ---------- OpenAI ----------------------------------------------------
  {
    key: 'openai_default_model',
    category: 'openai',
    editor: 'enum',
    label: 'Modelo padrão',
    options: OPENAI_MODEL_OPTIONS,
  },
  {
    key: 'openai_fallback_model',
    category: 'openai',
    editor: 'enum',
    label: 'Modelo fallback',
    options: OPENAI_MODEL_OPTIONS,
  },
  {
    key: 'openai_temperature',
    category: 'openai',
    editor: 'number',
    label: 'Temperatura',
    min: 0,
    max: 2,
    step: 0.1,
  },
  {
    key: 'openai_max_tokens',
    category: 'openai',
    editor: 'number',
    label: 'Max tokens',
    min: 1,
    max: 16000,
    step: 1,
  },
  {
    key: 'openai_timeout_ms',
    category: 'openai',
    editor: 'number',
    label: 'Timeout (ms)',
    min: 1,
    max: 60000,
    step: 500,
  },
  // ---------- Prompts ---------------------------------------------------
  {
    key: 'prompt_max_chars',
    category: 'prompts',
    editor: 'number',
    label: 'Tamanho máx. prompt',
    min: 100,
    max: 50000,
    step: 100,
  },
  {
    key: 'prompt_banned_patterns',
    category: 'prompts',
    editor: 'array',
    label: 'Padrões banidos (regex)',
    hint: 'Array JSON de regex JavaScript válidas.',
  },
  {
    key: 'anti_repetition_threshold',
    category: 'prompts',
    editor: 'number',
    label: 'Threshold anti-repetição',
    min: 0,
    max: 1,
    step: 0.05,
  },
  // ---------- Reagendamento --------------------------------------------
  {
    key: 'confirmation_c2_send_days_before',
    category: 'reagendamento',
    editor: 'number',
    label: 'C2: dias antes da reunião',
    min: 0,
    max: 7,
    step: 1,
    hint: '0 = envia no dia da reunião, 1 = véspera, etc. (padrão: 0).',
  },
  {
    key: 'confirmation_c3_hours_before',
    category: 'reagendamento',
    editor: 'number',
    label: 'C3: horas antes da reunião',
    min: 1,
    max: 48,
    step: 1,
    hint: 'Horas antes do horário da reunião para enviar o lembrete C3 (padrão: 1h).',
  },
  {
    key: 'confirmation_no_response_min_before',
    category: 'reagendamento',
    editor: 'number',
    label: 'Check não-resposta: min antes',
    min: 1,
    max: 120,
    step: 1,
    hint: 'Minutos antes da reunião para verificar se o contato confirmou presença (padrão: 15min).',
  },
  {
    key: 'reschedule_max',
    category: 'reagendamento',
    editor: 'number',
    label: 'Máx. reagendamentos',
    min: 0,
    max: 10,
    step: 1,
  },
  {
    key: 'reactivation_max',
    category: 'reagendamento',
    editor: 'number',
    label: 'Máx. reativações',
    min: 0,
    max: 10,
    step: 1,
  },
  {
    key: 'reactivation_thresholds',
    category: 'reagendamento',
    editor: 'array',
    label: 'Cadência reativação',
    hint: 'Array JSON de strings (ex: ["1d","3d","7d"]).',
  },
  {
    key: 'attendance_callback_delay_min',
    category: 'reagendamento',
    editor: 'number',
    label: 'Delay callback (min)',
    min: 0,
    max: 1440,
    step: 1,
  },
  // ---------- Calendar --------------------------------------------------
  {
    key: 'calendar_provider',
    category: 'calendar',
    editor: 'enum',
    label: 'Provider',
    options: CALENDAR_PROVIDER_OPTIONS,
    hint: 'Provider ativo para appointments (google ou ghl). Default: ghl.',
  },
  {
    key: 'ghl_location_id',
    category: 'calendar',
    editor: 'string',
    label: 'GHL Location ID',
    hint:
      'ID da location no GHL — necessário quando provider=ghl. Obter no painel GHL ' +
      '(Settings → Locations). Sem este valor, o adapter retorna erro ao criar appointment.',
  },
  {
    key: 'ghl_polling_enabled',
    category: 'calendar',
    editor: 'boolean',
    label: 'Polling GHL ativo',
    hint:
      'Ativa o envio de mensagens pelo cron GHL. ' +
      'False = modo scout: o cron continua rodando, descobre leads e salva contatos, ' +
      'mas NÃO envia mensagens. Quando ativado, apenas leads novos (não scouted) são contactados. ' +
      'True = fluxo completo com envio de mensagens.',
  },
  {
    key: 'ghl_poll_interval_minutes',
    category: 'calendar',
    editor: 'number',
    label: 'Intervalo de polling (min)',
    min: 1,
    max: 60,
    step: 1,
    hint: 'Minutos entre cada verificação do pipeline GHL (1..60). Default: 5.',
  },
  {
    key: 'ghl_appointment_sync_interval_minutes',
    category: 'calendar',
    editor: 'number',
    label: 'Intervalo de sync de agendamentos (min)',
    min: 1,
    max: 60,
    step: 1,
    hint: 'Minutos entre cada sincronização dos agendamentos do GHL (1..60). Default: 5.',
  },
  {
    key: 'ghl_user_allowlist',
    category: 'calendar',
    editor: 'array',
    label: 'Vendedores permitidos (GHL User IDs)',
    hint:
      'Array JSON de GHL User IDs. Vazio = sem restrição (todos os leads processados). ' +
      'Não-vazio = somente leads atribuídos a esses usuários são processados pelo cron.',
  },
  {
    key: 'ghl_user_calendar_map',
    category: 'calendar',
    editor: 'object',
    label: 'Mapa usuário → calendário (GHL)',
    hint:
      'JSON { "ghlUserId": "ghlCalendarId" } — direciona appointments para o ' +
      'calendário correto de cada vendedor ao criar agendamentos no GHL.',
  },
  {
    key: 'ghl_appointment_sync_enabled',
    category: 'calendar',
    editor: 'boolean',
    label: 'Sync de agendamentos ativo',
    hint:
      'Ativa o cron que sincroniza agendamentos do GHL para a tabela local `meetings`. ' +
      'False = cron é ignorado. True (padrão) = cron roda a cada 15 min.',
  },
  {
    key: 'ghl_last_appointment_sync_at',
    category: 'calendar',
    editor: 'string',
    label: 'Último sync de agendamentos',
    hint: 'ISO timestamp do último sync bem-sucedido. Escrito automaticamente pelo cron (leitura-only).',
  },
  // Story 10.5 (2026-07-28, user request) — indicador de vendas.
  {
    key: 'ghl_sale_stage_id',
    category: 'calendar',
    editor: 'string',
    label: 'Stage ID de "Venda realizada" (GHL)',
    hint:
      'ID do estágio, dentro da pipeline de ghl_lead_new_pipeline, que representa uma venda ' +
      'fechada. Confirmado via API do GHL — não alterar sem verificar de novo.',
  },
  {
    key: 'ghl_sale_product_field_id',
    category: 'calendar',
    editor: 'string',
    label: 'Custom field "Produtos" (GHL)',
    hint: 'ID do custom field da opportunity usado para extrair o produto vendido.',
  },
  {
    key: 'ghl_sale_sync_enabled',
    category: 'calendar',
    editor: 'boolean',
    label: 'Sync de vendas ativo',
    hint:
      'Ativa o cron que sincroniza vendas do GHL para a tabela local `sales`. ' +
      'False = cron é ignorado. True (padrão) = cron roda a cada 15 min.',
  },
  // Story 10.8 (2026-07-30, user request, AC7) — taxa de câmbio pro painel de ROI.
  {
    key: 'usd_to_brl_rate',
    category: 'operacao',
    editor: 'number',
    label: 'Taxa de câmbio USD→BRL',
    hint:
      'Usada pra converter o custo estimado de IA/WhatsApp (em USD) pro painel de ' +
      'ROI do dashboard (em BRL). Fixa — sem integração com API externa de câmbio.',
  },
  // Story 10.8 (2026-07-30, user request, AC4) — motivos de perda (GHL).
  {
    key: 'ghl_lost_stage_id',
    category: 'calendar',
    editor: 'string',
    label: 'Stage ID de "Perdido" (GHL)',
    hint:
      'ID do estágio, dentro da pipeline de ghl_lead_new_pipeline, que representa uma ' +
      'oportunidade perdida. Vazio = sincronização de motivos de perda desligada.',
  },
  {
    key: 'ghl_lost_reason_field_id',
    category: 'calendar',
    editor: 'string',
    label: 'Custom field "Motivo" (GHL)',
    hint:
      'ID do custom field da opportunity usado para extrair o motivo de perda. ' +
      'Vazio = sincronização de motivos de perda desligada.',
  },
  {
    key: 'ghl_sale_sync_interval_minutes',
    category: 'calendar',
    editor: 'number',
    label: 'Intervalo de sync de vendas (min)',
    min: 1,
    max: 60,
    step: 1,
    hint: 'Minutos entre cada sincronização de vendas do GHL (1..60). Default: 15.',
  },
  {
    key: 'ghl_last_sale_sync_at',
    category: 'calendar',
    editor: 'string',
    label: 'Último sync de vendas',
    hint: 'ISO timestamp do último sync bem-sucedido. Escrito automaticamente pelo cron (leitura-only).',
  },
]

/**
 * Returns the UI metadata for a key, or `undefined` when the key is not in
 * the editable set (e.g. internal keys like `seller_notification_templates`).
 */
export function getSettingMeta(key: string): SettingMeta | undefined {
  return SETTING_META.find((m) => m.key === key)
}

/**
 * Group settings by category, preserving the order in `CATEGORY_ORDER`. Keys
 * without metadata are dropped (intentional: admins should not edit internal
 * keys via the generic UI).
 */
export function groupByCategory<T extends { key: string }>(
  rows: ReadonlyArray<T>,
): Array<{ category: SettingCategory; label: string; items: Array<T & { meta: SettingMeta }> }> {
  const map = new Map<SettingCategory, Array<T & { meta: SettingMeta }>>()
  for (const row of rows) {
    const meta = getSettingMeta(row.key)
    if (meta === undefined) continue
    const list = map.get(meta.category) ?? []
    list.push({ ...row, meta })
    map.set(meta.category, list)
  }
  return CATEGORY_ORDER.filter((c) => map.has(c)).map((c) => ({
    category: c,
    label: CATEGORY_LABELS[c],
    items: map.get(c) ?? [],
  }))
}
